import { Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { tracking } from '#features/analytics';
import { Link } from '#features/routing';
import { Button, Image } from '#features/ui';
import { StoreNote } from '../StoreNote';

type HeroProps = {
  name: string;
  color: string;
  image: string;
};

const ACCENT_FALLBACK = '#4B50E6';

const isVisibleColor = (color: string) => {
  const c = color.toLowerCase();
  return c !== '#000000' && c !== '#000' && c !== 'black' && c !== '' && c !== 'transparent';
};

const scrollToHowItWorks = () => {
  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
};

export const Hero = ({ name, color, image }: HeroProps) => {
  const { t } = useLingui();
  const accentColor = isVisibleColor(color) ? color : ACCENT_FALLBACK;

  return (
    <Box position="relative" w="100%">
      {/* Ambient glow bleeding beyond the hero */}
      <Box
        position="absolute"
        top="-220px"
        left="-200px"
        right="-200px"
        bottom="-40px"
        background={`radial-gradient(ellipse 70% 55% at 72% 18%, ${accentColor}22 0%, transparent 68%)`}
        pointerEvents="none"
        zIndex={0}
      />

      <Flex
        alignItems="center"
        flexDir={{ base: 'column', lg: 'row' }}
        w="100%"
        gap={{ base: 2, lg: 6 }}
        position="relative"
        zIndex={1}
      >
        {/* Copy */}
        <Stack
          gap={{ base: 5, lg: 6 }}
          w={{ base: '100%', lg: '54%' }}
          flex="1"
          order={{ base: 2, lg: 1 }}
          alignItems={{ base: 'center', lg: 'flex-start' }}
          textAlign={{ base: 'center', lg: 'left' }}
          mt={{ base: 4, lg: 0 }}
        >
          <Heading
            as="h1"
            fontWeight="900"
            fontSize={{ base: '5xl', md: '6xl', xl: '7xl' }}
            lineHeight="0.92"
            letterSpacing="-0.03em"
            textTransform="uppercase"
          >
            {/* On a game page the H1 leads with "<Game> LFG" so the exact
                high-volume search phrase (e.g. "Valorant LFG") lives in the H1;
                the index keeps the brand headline. */}
            <Text as="span" display="block">
              {name ? `${name} LFG` : t`v3.hero.title1`}
            </Text>
            <Text as="span" display="block" color={accentColor}>
              {name ? t`v3.hero.title1` : t`v3.hero.title2`}
            </Text>
          </Heading>

          <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.7" color="fg.muted" maxW="500px">
            {/* Game pages lead the subtitle with "Find <Game> teammates" so the
                keyword phrase also sits high in the copy; index keeps the generic
                line. Inline macro interpolation binds the value on SSR + client. */}
            {name
              ? t`Find ${name} teammates for your next game. Post once or browse live LFG - your next squad is minutes away.`
              : t`v3.hero.subtitle`}
          </Text>

          <Flex
            gap={3}
            flexDir={{ base: 'column', sm: 'row' }}
            w={{ base: '100%', sm: 'auto' }}
            mt={1}
          >
            <Button
              minW="210px"
              w={{ base: '100%', sm: 'auto' }}
              size="lg"
              style={{ boxShadow: `0 0 26px ${accentColor}55` }}
              onClick={() => tracking.event({ action: 'click_get_started' })}
              asChild
            >
              <Link to="/{-$locale}/sign-up">{t`v3.hero.ctaPrimary`}</Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              color="fg.emphasized"
              borderColor="border"
              onClick={scrollToHowItWorks}
              _hover={{ borderColor: 'whiteAlpha.400', bg: 'whiteAlpha.50' }}
            >
              {t`v3.hero.ctaSecondary`}
            </Button>
          </Flex>

          <StoreNote />
        </Stack>

        {/* Character */}
        <Box
          w={{ base: '100%', lg: '46%' }}
          maxW={{ base: '420px', lg: '560px' }}
          height={{ base: '340px', sm: '440px', xl: '540px' }}
          position="relative"
          order={{ base: 1, lg: 2 }}
        >
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="115%"
            h="115%"
            background={`radial-gradient(circle at center, ${accentColor}22 0%, ${accentColor}08 42%, transparent 70%)`}
            zIndex={0}
            pointerEvents="none"
          />
          {image && (
            <Image
              key={image}
              src={image}
              sizes="(min-width: 1024px) 560px, 90vw"
              alt={name ? `${name} - Teamplay.gg` : 'Teamplay.gg'}
              loading="eager"
              fetchPriority="high"
              style={{
                objectFit: 'contain',
                height: '100%',
                width: '100%',
                position: 'relative',
                zIndex: 1,
                // Game hero images are rectangular photos with a hard bottom edge.
                // Fade the lower ~16% into transparency so the character dissolves
                // into the page background instead of reading as a hard crop -
                // works for any game regardless of source composition.
                maskImage: 'linear-gradient(to bottom, #000 84%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to bottom, #000 84%, transparent 100%)',
              }}
            />
          )}
        </Box>
      </Flex>
    </Box>
  );
};
